import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2, CalendarDays, Send, CheckCircle2 } from 'lucide-react';
import PortalLayout from '../../pages/portal/PortalLayout';

const API = process.env.REACT_APP_BACKEND_URL;
let cache = null;
let inflight = null;
const listeners = new Set();

function load(force = false) {
  if (cache && !force) return Promise.resolve(cache);
  if (!inflight || force) {
    inflight = axios.get(`${API}/api/portal/tool-access`, { withCredentials: true })
      .then((r) => { cache = r.data; listeners.forEach((l) => l(cache)); return cache; })
      .catch(() => { cache = { tools: {}, labels: {}, pending: [], error: true }; return cache; })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useToolAccess() {
  const [data, setData] = useState(cache);
  useEffect(() => {
    listeners.add(setData);
    load().then(setData);
    return () => listeners.delete(setData);
  }, []);
  return { data, loading: !data, has: (k) => !!data?.tools?.[k], pending: (k) => (data?.pending || []).includes(k), refresh: () => load(true) };
}

export function LockedTool({ tool, compact = false }) {
  const { data, pending, refresh } = useToolAccess();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const label = data?.labels?.[tool] || 'This tool';
  const isPending = pending(tool);
  const request = async () => {
    setBusy(true);
    try { await axios.post(`${API}/api/portal/tool-access/request`, { tool, note }, { withCredentials: true }); await refresh(); toast.success('Request sent! We will let you know when it is unlocked.'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not send request'); }
    finally { setBusy(false); }
  };
  return (
    <div className={`rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-white to-[#F8FAFC] ${compact ? 'p-5' : 'p-8 sm:p-10'} text-center`} data-testid={`locked-tool-${tool}`}>
      <span className="inline-flex w-12 h-12 rounded-2xl bg-[#FFF7ED] text-[#F97316] items-center justify-center mb-3"><Lock size={22} /></span>
      <h3 className="text-lg font-black text-[#0A0F1C]">{label}</h3>
      <p className="text-sm text-[#64748B] max-w-md mx-auto mt-1">This tool is part of your coaching plan. Request access and your strategist will unlock it, or book a call to talk about which tools fit your goals.</p>
      {isPending ? (
        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-2" data-testid={`locked-tool-pending-${tool}`}><CheckCircle2 size={15} /> Request sent, we'll be in touch</div>
      ) : (
        <>
          {!compact && <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything you'd like us to know? (optional)" data-testid={`locked-tool-note-${tool}`}
            className="mt-4 w-full max-w-md mx-auto block px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />}
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button type="button" onClick={request} disabled={busy} data-testid={`request-access-${tool}`}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1D4ED8] text-white text-sm font-bold hover:bg-[#1E40AF] disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Request access
            </button>
            <Link to="/portal/book" data-testid={`book-call-${tool}`} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-[#E2E8F0] text-[#0A0F1C] text-sm font-bold hover:border-[#1D4ED8]">
              <CalendarDays size={15} /> Book a call
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

export default function ToolGate({ tool, children, compact = false }) {
  const { loading, has } = useToolAccess();
  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#1D4ED8]" size={22} /></div>;
  return has(tool) ? children : <LockedTool tool={tool} compact={compact} />;
}

export function GatedPage({ tool, children }) {
  const { loading, has } = useToolAccess();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#1D4ED8]" size={24} /></div>;
  if (has(tool)) return children;
  return <PortalLayout><div className="max-w-3xl mx-auto px-5 py-10"><LockedTool tool={tool} /></div></PortalLayout>;
}
